import AppButton from '@nxsys/components/AppButton';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import { AppConfirmation } from '@nxsys/components/AppConfirmationModal';
import {
  useMarkAllAsPaidInvoice,
  useMarkAsPaidInvoice,
} from '@nxsys/hooks/api/invoice/useInvoiceApi';
import { AppState } from '@nxsys/store';
import { dashboardActions } from '@nxsys/store/slices/dashboard/reducer/dashboardSlice';
import { GetInvoicingListParams } from '@nxsys/store/slices/dashboard/types';
import { Invoice } from '@nxsys/types/models/invoice';
import { formatStatus, showCurrencyByCountry } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { ColumnsType } from 'antd/lib/table';
import MarkAsPaidModal from '../../invoices/components/MarkAsPaidModal';
import moment from 'moment';
import numeral from 'numeral';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toNumber } from 'lodash';

export enum InvoicingModalTypeEnum {
  OUTSTANDING_INVOICES = 'OUTSTANDING_INVOICES',
  PAID_INVOICES = 'PAID_INVOICES',
  CREDIT_INVOICES = 'CREDIT_INVOICES',
}

type Props = {
  isOpen: boolean;
  toggle: () => void;
  cbAfterAction: () => void;
  type: InvoicingModalTypeEnum;
};

const InvoicingModal: React.FC<Props> = ({
  isOpen,
  toggle,
  cbAfterAction,
  type,
}) => {
  const dispatch = useDispatch();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const { invoicingList } = useSelector(
    (state: AppState) => state.dashboardStore,
  );

  const getCurrencySymbol = (currency = 'GBP') => {
    switch (currency) {
      case 'GBP':
        return showCurrencyByCountry(payrollCountry);
      case 'EUR':
        return '€';
      case 'USD':
        return '$';
      default:
        return showCurrencyByCountry(payrollCountry);
    }
  };

  const formatMoney = (number: number, currency = 'GBP') => {
    const currencySymbol = getCurrencySymbol(currency);
    if (number && numeral(number).format('0,0.00') !== 'NaN') {
      return `${currencySymbol} ${numeral(number).format('0,0.00')}`;
    }
    if (number === 0) {
      return `${currencySymbol} 0.00`;
    }
    return '_';
  };
  const { list, pagination, loading, queryParams } = invoicingList;

  const [selectedRows, setSelectedRows] = useState<Invoice[]>([]);

  const [openMarkAsPaidModal, setOpenMarkAsPaidModal] = useState(false);

  useEffect(() => {
    if (isOpen)
      handleFetchData({
        page: 0,
        size: 20,
      });
  }, [isOpen]);

  const handleFetchData = (params: GetInvoicingListParams) => {
    const newParams = {
      ...params,
    };
    if (type === InvoicingModalTypeEnum.OUTSTANDING_INVOICES) {
      newParams.isOutstanding = true;
    } else if (type === InvoicingModalTypeEnum.PAID_INVOICES) {
      newParams.isPaid = true;
    } else if (type === InvoicingModalTypeEnum.CREDIT_INVOICES) {
      newParams.isCredit = true;
    }
    dispatch(dashboardActions.getInvoicingList(newParams));
  };

  const refetchData = () => {
    handleFetchData({
      ...queryParams,
    });
    cbAfterAction();
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const columns: ColumnsType<Invoice> = [
    {
      key: 'invoiceNo',
      title: 'Inv No',
      dataIndex: 'invoiceNo',
      width: 160,
    },
    {
      key: 'invoicedDate',
      title: 'Date',
      align: 'right',
      dataIndex: 'invoicedDate',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      width: 140,
      render: (value) => (
        <span>
          {value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '_'}
        </span>
      ),
    },
    {
      key: 'companyRef',
      title: 'From',
      dataIndex: 'companyRef',
      width: 240,
      render: (value) => value?.companyName || '',
    },
    {
      key: 'agencyRef',
      title: 'To',
      dataIndex: 'agencyRef',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => <span>{value?.name || ''}</span>,
      width: 350,
    },
    {
      key: 'totalNet',
      title: 'Net',
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (record) => (
        <span>{formatMoney(record?.totalNet, record?.currency)}</span>
      ),
      width: 140,
    },
    {
      key: 'totalVat',
      title: 'VAT',
      align: 'right',
      render: (record) => (
        <span>{formatMoney(record?.totalVat, record?.currency)}</span>
      ),
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      width: 140,
    },
    {
      key: 'totalPrices',
      title: 'Total',
      align: 'right',
      render: (record) => (
        <span>{formatMoney(record?.totalPrices, record?.currency)}</span>
      ),
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      width: 140,
    },
    {
      key: 'receipt',
      title: 'Paid',
      align: 'right',
      render: (record) => (
        <span>{formatMoney(record?.receipt || 0, record?.currency)}</span>
      ),
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      width: 140,
    },
    {
      key: 'outstanding',
      title: 'Outstanding',
      align: 'right',
      render: (record) => (
        <span>{formatMoney(record?.outstanding, record?.currency)}</span>
      ),
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      width: 140,
    },
    {
      key: 'sentStatus',
      title: 'Sent Status',
      dataIndex: 'sentStatus',
      align: 'center',
      render: (data) => (
        <AppTagStatus className={formatStatus(data || 'Inactive')}>
          {formatStatus(data || 'Inactive')}
        </AppTagStatus>
      ),
      width: 140,
    },
  ];

  const handleOpenMarkAsPaidModal = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: 'Please select at least one invoice to mark as paid',
      });
      return;
    }
    setOpenMarkAsPaidModal(true);
  };
  const { run: markAsPaidInvoiceRun, loading: isMarkAsPaidInvoicePending } =
    useMarkAsPaidInvoice();
  const {
    run: markAllAsPaidInvoiceRun,
    loading: isMarkAllAsPaidInvoicePending,
  } = useMarkAllAsPaidInvoice();
  const handleMarkAsPaid = async (
    datePaid: string,
    receipt: number,
    reference: string,
  ) => {
    const res = await markAsPaidInvoiceRun({
      ids: selectedRows.map((item) => item.id),
      datePaid,
      receipt: toNumber(receipt),
      reference,
    });
    if (res.success) {
      showMessage({
        type: 'success',
        content: 'Mark as paid successfully',
      });
      setOpenMarkAsPaidModal(false);
      refetchData();
    } else {
      showMessage({
        type: 'error',
        content: 'Mark as paid failed',
      });
    }
  };

  const handleMarkAllAsPaid = () => {
    if (!list || list.length === 0) {
      showMessage({
        type: 'warning',
        content: 'No outstanding invoices to mark as paid',
      });
      return;
    }
    AppConfirmation.open({
      title: 'Mark All as Paid',
      paragraph:
        'Are you sure you want to mark all outstanding invoices as paid?',
      type: 'warning',
      icon: 'IconWarning',
      okText: 'Confirm',
      onConfirm: async () => {
        const res = await markAllAsPaidInvoiceRun({
          datePaid: new Date() as any,
          receipt: 0,
        });
        if (res.success) {
          showMessage({
            type: 'success',
            content: 'Mark all as paid successfully',
          });
          refetchData();
        } else {
          showMessage({
            type: 'error',
            content: 'Mark all as paid failed',
          });
        }
      },
    });
  };

  return (
    <AppModalContainer
      title={
        <div className="flex items-center justify-between w-full gap-x-[8px]">
          <div className="whitespace-nowrap">
            {type === InvoicingModalTypeEnum.OUTSTANDING_INVOICES
              ? 'Outstanding Invoices'
              : type === InvoicingModalTypeEnum.PAID_INVOICES
                ? 'Paid Invoices'
                : 'Credit Invoices'}
          </div>
          {type === InvoicingModalTypeEnum.OUTSTANDING_INVOICES && (
            <div className="flex items-center justify-end gap-2 w-full flex-wrap">
              <AppButton
                variant="outlined"
                name="Mark all as Paid"
                onClick={handleMarkAllAsPaid}
                loading={isMarkAllAsPaidInvoicePending}
              />
              <AppButton
                name="Mark as Paid"
                onClick={handleOpenMarkAsPaidModal}
              />
            </div>
          )}
        </div>
      }
      isOpen={isOpen}
      toggle={toggle}
      hiddenFooter
      className="max-sm:w-[100vw]"
      width={1200}
      minHeight="0"
      noPaddingBody
      titleFullWidth
    >
      <AppsContent>
        <AppTableContainer
          rowKey="id"
          data={list ?? []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 2000 }}
          columns={columns}
          onChangePagination={onChangePagination}
          rowSelection={{
            type: 'checkbox',
            onChange: (_: React.Key[], selectedRows: Invoice[]) => {
              setSelectedRows(selectedRows);
            },
          }}
        />
      </AppsContent>
      <MarkAsPaidModal
        isOpen={openMarkAsPaidModal}
        toggle={() => setOpenMarkAsPaidModal(false)}
        onSubmit={handleMarkAsPaid}
        isMarkAsPaidPending={isMarkAsPaidInvoicePending}
        data={selectedRows}
      />
    </AppModalContainer>
  );
};

export default InvoicingModal;
